import { useCallback, useState } from 'react';

import type {
  BriefArtifactRef,
  BriefTemplateIntakeValues,
  BriefTemplateSubmitPayload,
} from '../brief-template-intake.types';

const INITIAL_VALUES: BriefTemplateIntakeValues = {
  title: '',
  instructions: '',
  files: [],
  artifacts: [],
};

export interface UseBriefTemplateIntakeFormReturn {
  values: BriefTemplateIntakeValues;
  /** `true` when `title.trim().length > 0`. Add-context fields do not gate validity. */
  isValid: boolean;
  setTitle: (title: string) => void;
  setInstructions: (instructions: string) => void;
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  /** Merges incoming refs into the current selection, dedup'd by id (first occurrence wins). */
  addArtifacts: (refs: BriefArtifactRef[]) => void;
  removeArtifact: (id: string) => void;
  reset: () => void;
  getSubmitPayload: () => Readonly<BriefTemplateSubmitPayload>;
}

const dedupeArtifacts = (
  current: BriefArtifactRef[],
  incoming: BriefArtifactRef[],
): BriefArtifactRef[] => {
  const seen = new Set(current.map((a) => a.id));
  const next = [...current];
  for (const ref of incoming) {
    if (seen.has(ref.id)) continue;
    seen.add(ref.id);
    next.push(ref);
  }
  return next;
};

export const useBriefTemplateIntakeForm = (): UseBriefTemplateIntakeFormReturn => {
  const [values, setValues] = useState<BriefTemplateIntakeValues>(INITIAL_VALUES);

  const isValid = values.title.trim().length > 0;

  const setTitle = useCallback((title: string) => setValues((v) => ({ ...v, title })), []);

  const setInstructions = useCallback(
    (instructions: string) => setValues((v) => ({ ...v, instructions })),
    [],
  );

  const addFiles = useCallback(
    (files: File[]) => setValues((v) => ({ ...v, files: [...v.files, ...files] })),
    [],
  );

  const removeFile = useCallback(
    (index: number) =>
      setValues((v) => ({
        ...v,
        files: v.files.filter((_, i) => i !== index),
      })),
    [],
  );

  const addArtifacts = useCallback(
    (refs: BriefArtifactRef[]) =>
      setValues((v) => ({ ...v, artifacts: dedupeArtifacts(v.artifacts, refs) })),
    [],
  );

  const removeArtifact = useCallback(
    (id: string) =>
      setValues((v) => ({
        ...v,
        artifacts: v.artifacts.filter((a) => a.id !== id),
      })),
    [],
  );

  const reset = useCallback(() => setValues(INITIAL_VALUES), []);

  const getSubmitPayload = useCallback((): Readonly<BriefTemplateSubmitPayload> => {
    const trimmed = values.title.trim();
    if (trimmed.length === 0) {
      throw new Error(
        'useBriefTemplateIntakeForm.getSubmitPayload: form is invalid (title is empty)',
      );
    }
    return Object.freeze({
      name: trimmed,
      instructions: values.instructions,
      files: values.files,
      artifactIds: values.artifacts.map((a) => a.id),
    });
  }, [values]);

  return {
    values,
    isValid,
    setTitle,
    setInstructions,
    addFiles,
    removeFile,
    addArtifacts,
    removeArtifact,
    reset,
    getSubmitPayload,
  };
};
